import React, { FC, useEffect, useState } from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import styles from "./styles/WorkOrderDNDItem.module.scss";
import { statusIconsWorkOrderDND } from "../constants";
import cn from "classnames";
import { toCapitalize } from "Shared/Utils/shared";
import { WorkOrderDNDDraggable } from "./WorkOrderDNDDraggable/WorkOrderDNDDraggable";
import { useSelector } from "react-redux";
import {
  selectDashboardWorkOrder,
  selectDashboardWorkOrderLoading,
} from "Redux/Dashboard/slice";
import {
  DashboardWorkOrderData,
  DashboardWorkOrderResult,
} from "Shared/Types/dashboard";
import {
  editWorkOrder,
  switchWorkOrderStatusToComplete,
  switchWorkOrderStatusToOpen,
  switchWorkOrderStatusToPending,
} from "Redux/WorkOrder/reducer";
import { useAppDispatch } from "Redux/store";
import { getDashboardWorkOrder } from "Redux/Dashboard/reducer";
import { selectEditWorkOrderLoading } from "Redux/WorkOrder/slice";
import { useWorkOrderDND } from "../useWorkOrderDND";
import { useNavigate } from "react-router";
import { getRoutWithId } from "Shared/Constants/routeIdenicator";
import { WORK_ORDERS } from "Shared/Constants/routes";
import { scrollToTop } from "Shared/Utils/dashboard";
import SkeletonLoader from "Components/SkeletonLoader/SkeletonLoader";
import { Status6AbEnum } from "Shared/Types/fleetmanagementapi";
import { notifyError } from "Components/Notify/notify";
import { onError } from "Components/Vendor/VendorForm/utils";

export const WorkOrderDNDItem: FC = () => {
  const workOrder = useSelector(selectDashboardWorkOrder);
  const workOrderLoading = useSelector(selectDashboardWorkOrderLoading);
  const dispatch = useAppDispatch();
  const editLoading = useSelector(selectEditWorkOrderLoading);
  const { workOrderParams } = useWorkOrderDND();
  const navigate = useNavigate();

  const [staticStatus, setStaticStatus] = useState<
    { status: string; work_orders: DashboardWorkOrderData[] }[]
  >([]);

  const status = ["open", "pending", "completed"];

  const updateStaticStatus = (workOrdersData: DashboardWorkOrderResult[]) => {
    const updatedStatus = status.map((statusGroup) => ({
      status: statusGroup,
      work_orders: workOrdersData.reduce<DashboardWorkOrderData[]>(
        (acc, workOrderGroup) => {
          const filteredWorkOrders = workOrderGroup.work_orders.filter(
            (order) => order.status === statusGroup
          );
          acc.push(...filteredWorkOrders);
          return acc;
        },
        []
      ),
    }));
    setStaticStatus(updatedStatus);
  };

  useEffect(() => {
    if (workOrder.results) {
      updateStaticStatus(workOrder.results);
    }
  }, [workOrder.results]);

  // Функция для изменения статуса
  const onChangeStatuses = (id: string, optionKey: `${Status6AbEnum}`) => {
    if (!id) return;

    const onSuccess = () => dispatch(getDashboardWorkOrder(workOrderParams));

    const onErrorStatus = (error: any) => {
      notifyError(error);
    };
    if (optionKey === "open") {
      return dispatch(switchWorkOrderStatusToOpen({ id, onSuccess, onError }));
    }
    if (optionKey === "pending") {
      return dispatch(
        switchWorkOrderStatusToPending({ id, onSuccess, onError })
      );
    }
    if (optionKey === "completed") {
      return dispatch(
        switchWorkOrderStatusToComplete({
          id,
          onSuccess,
          onError: onErrorStatus,
        })
      );
    }
  };

  const handleOnDragEnd = async (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      notifyError("No destination provided.");
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      notifyError("Dropped in the same location, ignoring.");
      return;
    }

    const sourceGroupIndex = Number(source.droppableId);
    const destinationGroupIndex = Number(destination.droppableId);

    if (
      sourceGroupIndex < 0 ||
      sourceGroupIndex >= staticStatus.length ||
      destinationGroupIndex < 0 ||
      destinationGroupIndex >= staticStatus.length
    ) {
      notifyError("Invalid source or destination group index.");
      return;
    }

    const sourceGroup = staticStatus[sourceGroupIndex];
    const destinationGroup = staticStatus[destinationGroupIndex];

    if (
      sourceGroup.status === "completed" &&
      destinationGroup.status !== "completed"
    ) {
      notifyError(
        "You cannot change a task's status from 'Completed' to another status."
      );
      return;
    }

    const sourceWorkOrders = [...sourceGroup.work_orders];
    const destinationWorkOrders = [...destinationGroup.work_orders];

    const [movedItem] = sourceWorkOrders.splice(source.index, 1);

    const updatedMovedItem = {
      ...movedItem,
      status: destinationGroup.status,
    };

    destinationWorkOrders.splice(
      destination.index,
      0,
      updatedMovedItem as DashboardWorkOrderData
    );

    const updatedStaticStatus = [...staticStatus];
    updatedStaticStatus[sourceGroupIndex].work_orders = sourceWorkOrders;
    updatedStaticStatus[destinationGroupIndex].work_orders =
      destinationWorkOrders;

    setStaticStatus(updatedStaticStatus);

    if (updatedMovedItem.id) {
      onChangeStatuses(
        updatedMovedItem.id,
        updatedMovedItem.status as `${Status6AbEnum}`
      );
    }
  };

  const redirectToDetailWorkOrder = (id: string) => {
    scrollToTop();
    navigate(getRoutWithId(WORK_ORDERS, id));
  };

  return (
    <DragDropContext onDragEnd={handleOnDragEnd}>
      {editLoading || workOrderLoading ? (
        <SkeletonLoader
          className={styles.skeleton}
          height="127px"
          variant="grid"
          amountOfBlocks={9}
          itemXs={4}
        />
      ) : (
        <section className={styles.wrapper}>
          {staticStatus?.map((workOrderGroup, groupIndex) => (
            <div key={groupIndex} className={styles.wrapper__item}>
              <div
                className={cn(
                  styles.statusWrapper,
                  styles[`statusWrapper__${groupIndex}`]
                )}
              >
                <div className={styles.statusImg}>
                  {statusIconsWorkOrderDND[groupIndex]}
                </div>
                <h2 className={styles.statusText}>
                  {toCapitalize(workOrderGroup.status)}
                </h2>
              </div>
              <Droppable droppableId={String(groupIndex)}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={styles.workOrderWrapper}
                  >
                    {workOrderGroup.work_orders.map((workOrderItem, index) => (
                      <WorkOrderDNDDraggable
                        key={workOrderItem.id}
                        navigate={redirectToDetailWorkOrder}
                        workOrderItem={workOrderItem}
                        index={index}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </section>
      )}
    </DragDropContext>
  );
};
